import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, ChevronRight, Linkedin, Twitter, Facebook } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Ana Sayfa', path: '/' },
    { name: 'Hakkımızda', path: '/hakkimizda' },
    { name: 'Hizmetlerimiz', path: '/hizmetler' },
    { name: 'Blog', path: '/blog' },
    { name: 'Kariyer', path: '/kariyer' },
    { name: 'İletişim', path: '/iletisim' },
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="section-container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-10 h-10 rounded-xl bg-[#2A9D8F] flex items-center justify-center">
              <span className="text-white font-bold text-lg font-['Outfit']">B</span>
            </div>
            <div>
              <span className="text-xl font-bold text-[#264653] font-['Outfit']">BDHA</span>
              <span className="text-xs block text-[#264653]/60 -mt-1">Danışmanlık</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link ${location.pathname === link.path ? 'text-[#2A9D8F]' : ''}`}
                data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a href="tel:02169989030" className="flex items-center gap-2 text-[#264653]">
              <Phone size={18} className="text-[#2A9D8F]" />
              <span className="font-medium">0216 998 90 30</span>
            </a>
            <Link to="/iletisim" className="btn-primary" data-testid="navbar-contact-btn">
              Teklif Al
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="mobile-menu lg:hidden animate-fade-in">
          <div className="flex justify-between items-center mb-8">
            <Link to="/" className="flex items-center gap-3" onClick={() => setIsOpen(false)}>
              <div className="w-10 h-10 rounded-xl bg-[#2A9D8F] flex items-center justify-center">
                <span className="text-white font-bold text-lg font-['Outfit']">B</span>
              </div>
              <span className="text-xl font-bold text-[#264653] font-['Outfit']">BDHA</span>
            </Link>
            <button onClick={() => setIsOpen(false)} data-testid="close-mobile-menu">
              <X size={24} />
            </button>
          </div>
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-lg font-medium text-[#264653] py-2 border-b border-[#264653]/10"
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </Link>
            ))}
          </div>
          <div className="mt-8">
            <a href="tel:02169989030" className="flex items-center gap-2 text-[#264653] mb-4">
              <Phone size={18} className="text-[#2A9D8F]" />
              <span className="font-medium">0216 998 90 30</span>
            </a>
            <Link
              to="/iletisim"
              className="btn-primary w-full flex justify-center"
              onClick={() => setIsOpen(false)}
            >
              Teklif Al
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#264653] text-white" data-testid="footer">
      <div className="section-container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#2A9D8F] flex items-center justify-center">
                <span className="text-white font-bold text-lg font-['Outfit']">B</span>
              </div>
              <div>
                <span className="text-xl font-bold font-['Outfit']">BDHA</span>
                <span className="text-xs block text-white/60 -mt-1">Danışmanlık</span>
              </div>
            </div>
            <p className="text-white/70 leading-relaxed mb-6">
              İnsan kaynakları ve personel temini alanında profesyonel çözümler sunarak işletmelerin
              büyümesine katkı sağlıyoruz.
            </p>
            <div className="flex gap-4">
              <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#2A9D8F] transition-colors">
                <Linkedin size={18} />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#2A9D8F] transition-colors">
                <Twitter size={18} />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#2A9D8F] transition-colors">
                <Facebook size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-6 font-['Outfit']">Hızlı Bağlantılar</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/hizmetler" className="text-white/70 hover:text-[#2A9D8F] transition-colors flex items-center gap-2">
                  <ChevronRight size={16} /> Hizmetlerimiz
                </Link>
              </li>
              <li>
                <Link to="/hakkimizda" className="text-white/70 hover:text-[#2A9D8F] transition-colors flex items-center gap-2">
                  <ChevronRight size={16} /> Hakkımızda
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-white/70 hover:text-[#2A9D8F] transition-colors flex items-center gap-2">
                  <ChevronRight size={16} /> Blog
                </Link>
              </li>
              <li>
                <Link to="/kariyer" className="text-white/70 hover:text-[#2A9D8F] transition-colors flex items-center gap-2">
                  <ChevronRight size={16} /> Kariyer
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold text-lg mb-6 font-['Outfit']">Hizmetlerimiz</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/hizmetler" className="text-white/70 hover:text-[#2A9D8F] transition-colors flex items-center gap-2">
                  <ChevronRight size={16} /> Personel Temini
                </Link>
              </li>
              <li>
                <Link to="/hizmetler" className="text-white/70 hover:text-[#2A9D8F] transition-colors flex items-center gap-2">
                  <ChevronRight size={16} /> Bordrolama
                </Link>
              </li>
              <li>
                <Link to="/hizmetler" className="text-white/70 hover:text-[#2A9D8F] transition-colors flex items-center gap-2">
                  <ChevronRight size={16} /> İK Danışmanlığı
                </Link>
              </li>
              <li>
                <Link to="/hizmetler" className="text-white/70 hover:text-[#2A9D8F] transition-colors flex items-center gap-2">
                  <ChevronRight size={16} /> İSG Danışmanlığı
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-lg mb-6 font-['Outfit']">İletişim</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={20} className="text-[#2A9D8F] shrink-0 mt-1" />
                <span className="text-white/70">
                  Kavacık Mah. Fatih Sultan Mehmet Cad. Tonoğlu İş Merkezi No:3/4 Beykoz/İSTANBUL
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={20} className="text-[#2A9D8F]" />
                <a href="tel:02169989030" className="text-white/70 hover:text-white">
                  0216 998 90 30
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={20} className="text-[#2A9D8F]" />
                <a href="mailto:ik@bdhadanismanlik.com" className="text-white/70 hover:text-white">
                  ik@bdhadanismanlik.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/60 text-sm">
            © {currentYear} BDHA Danışmanlık. Tüm hakları saklıdır.
          </p>
          <div className="flex gap-6 text-sm">
            <a href="#" className="text-white/60 hover:text-white">Gizlilik Politikası</a>
            <a href="#" className="text-white/60 hover:text-white">KVKK</a>
            <a href="#" className="text-white/60 hover:text-white">Çerez Politikası</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

export default Layout;
